import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateResource } from '../../api/production-request-api/index.js';
import { toast } from '@pella/sash-framework';
import { QUERY_KEYS } from '../../data/constants';
import { useAppContext } from '../../providers';

/**
 * Hook containing mutation function for updating a Resource.
 */
export const useUpdateResource = () => {
  const queryClient = useQueryClient();
  const { selectedPlant } = useAppContext();
  const queryKey = [QUERY_KEYS.RESOURCE_DATA, selectedPlant, { resourceQueues: true }];

  return useMutation({
    /**
     * mutateFn - Function to call updateResource.
     * @param {object} params
     * @param {Pella.ProductionRequestAPI.Plant['id']} params.id - The ID of the Resource to update.
     * @param {Pella.ProductionRequestAPI.UpdateResourceInput} params.input - The inputs for updating the Resource.
     */
    mutationFn: ({ id, input }) => updateResource(id, input),
    onMutate: async ({ id, input }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousResources = queryClient.getQueryData(queryKey);

      // Optimistically update the resource data in the cache
      queryClient.setQueryData(queryKey, (/** @type {Pella.ProductionRequestAPI.Resource[]} */ oldData) => {
        // Guard against undefined data - if no data in cache, return empty array or previous data
        if (!oldData || !Array.isArray(oldData)) {
          return previousResources || [];
        }
        return oldData.map((resource) => (resource.id === id ? { ...resource, ...input } : resource));
      });

      // Return a result with the snapshotted value
      return { previousResources };
    },
    onError: (_err, _variables, onMutateResult) => {
      // Rollback to the previous value on error
      queryClient.setQueryData(queryKey, onMutateResult?.previousResources);
    },
    onSuccess: () => {
      toast.dismiss();
      toast.success('Resource updated successfully!');

      // Invalidate queries to refetch the updated data
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCE_DATA, selectedPlant] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUEUE_DATA, selectedPlant] });
    },
  });
};
